import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';

// 🌐 한글 감지 정규식
const koreanRegex = /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/;

interface CustomDomainData {
  domain: string;
  status: 'pending' | 'verified' | 'failed';
  createdAt: any;
  verifiedAt?: any;
}

const CustomDomainSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { canUseCustomDomain, isPro, isExpired } = useSubscription();
  const { user } = useAuth();
  
  // URL 파라미터에서 정보 추출
  const searchParams = new URLSearchParams(location.search);
  const cardId = searchParams.get('cardId') || '';
  const initialDomain = searchParams.get('initialDomain') || '';
  
  const [customDomain, setCustomDomain] = useState(initialDomain);
  const [isValidDomain, setIsValidDomain] = useState(false);
  const [customDomainLoading, setCustomDomainLoading] = useState(false);
  const [verifyingDomain, setVerifyingDomain] = useState(false);
  const [existingDomainData, setExistingDomainData] = useState<CustomDomainData | null>(null);
  const [dnsGuideVisible, setDnsGuideVisible] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showKoreanWarning, setShowKoreanWarning] = useState(false);
  
  const userId = user?.uid;
  
  // Domain validation regex
  const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9])*$/;

  // 🔒 커스텀 도메인 접근 제한 확인
  useEffect(() => {
    if (!canUseCustomDomain) {
      // Pro 플랜이 아닌 경우 Paywall로 리다이렉트
      navigate('/paywall', { 
        state: { limitType: 'custom_domain' }
      });
    }
  }, [canUseCustomDomain, navigate]);

  useEffect(() => {
    if (userId && cardId) {
      fetchExistingDomain();
    }
    // Initialize validation for existing values
    if (initialDomain) {
      validateDomain(initialDomain);
    }
  }, [userId, cardId, initialDomain]);

  const fetchExistingDomain = async () => {
    if (!userId || !cardId) return;
    setCustomDomainLoading(true);
    try {
      const docRef = doc(db, 'users', userId, 'cards', cardId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data?.customDomain) {
          setExistingDomainData({
            domain: data.customDomain,
            status: data.domainStatus || 'pending',
            createdAt: data.updatedAt || data.createdAt
          });
          setCustomDomain(data.customDomain);
          validateDomain(data.customDomain);
        }
      }
    } catch (error) {
      console.error('Error fetching existing custom domain:', error);
      showSnackbar(t('domainSettings.fetchError'));
    } finally {
      setCustomDomainLoading(false);
    }
  };

  const validateDomain = (input: string) => {
    const trimmed = input.trim().toLowerCase();
    const valid = domainRegex.test(trimmed) && trimmed.length >= 4 && trimmed.length <= 253;
    setIsValidDomain(valid);
    return valid;
  };

  const handleDomainChange = (text: string) => {
    // 🚨 한글 입력 감지 및 처리
    if (koreanRegex.test(text)) {
      setShowKoreanWarning(true);
      showSnackbar(t('domainSettings.koreanInputDetected'));
      setTimeout(() => setShowKoreanWarning(false), 3000);
      return;
    }
    
    // 도메인에서 프로토콜과 www 제거 후 허용된 문자만 유지
    const cleanedText = text.replace(/^https?:\/\//, '').replace(/^www\./, '');
    const filteredText = cleanedText.replace(/[^a-zA-Z0-9.-]/g, '');
    const lowercasedText = filteredText.toLowerCase();
    setCustomDomain(lowercasedText);
    validateDomain(lowercasedText);
    setShowKoreanWarning(false);
  };

  const saveCustomDomain = async () => {
    if (!userId || !customDomain) return;
    
    if (!validateDomain(customDomain)) {
      showSnackbar(t('domainSettings.domainValidation.invalidFormat'));
      return;
    }
    
    setCustomDomainLoading(true);
    try {
      const domainToSave = customDomain.trim().toLowerCase();
      
      // 카드 문서에 customDomain 저장
      await setDoc(doc(db, 'users', userId, 'cards', cardId), {
        customDomain: domainToSave,
        domainStatus: 'pending',
        updatedAt: serverTimestamp(),
      }, { merge: true });

      showSnackbar(t('domainSettings.domainSaved'));
      
      // 성공 시 이전 화면으로 돌아가기
      navigate(-1);
    } catch (error) {
      console.error('Error saving custom domain:', error);
      showSnackbar(t('domainSettings.saveError'));
    } finally {
      setCustomDomainLoading(false);
    }
  };

  const verifyCustomDomain = async () => {
    if (!userId || !customDomain) return;
    
    setVerifyingDomain(true);
    try {
      // Update status to pending for manual verification by admin
      const domainData: CustomDomainData = {
        domain: customDomain.trim().toLowerCase(),
        status: 'pending',
        createdAt: existingDomainData?.createdAt || serverTimestamp(),
      };

      await setDoc(doc(db, 'users', userId, 'customDomain', 'info'), domainData);

      setExistingDomainData(domainData);
      showSnackbar('Domain submitted for manual verification. Our team will verify within 24-48 hours.');

    } catch (error) {
      console.error('Error verifying custom domain:', error);
      showSnackbar(t('domainSettings.domainValidation.verificationFailed'));
    } finally {
      setVerifyingDomain(false);
    }
  };

  const showSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarVisible(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified': return '#4caf50';
      case 'pending': return '#ff9800';
      case 'failed': return '#f44336';
      default: return '#757575';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'verified': return t('domainSettings.statusVerified');
      case 'pending': return t('domainSettings.statusPending');
      case 'failed': return t('domainSettings.statusFailed');
      default: return '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('domainSettings.customDomainTitle')}
          </h1>
          <p className="text-gray-600">
            {t('domainSettings.customDomainDescription')}
          </p>
        </div>

        {/* DNS Setup Guide Section - 상단에 배치 */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {t('domainSettings.dnsGuide.title')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('domainSettings.dnsGuide.subtitle')}
          </p>
          
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4">
                1
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-1">{t('domainSettings.dnsGuide.step1Title')}</h3>
                <p className="text-gray-600">{t('domainSettings.dnsGuide.step1Description')}</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4">
                2
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-1">{t('domainSettings.dnsGuide.step2Title')}</h3>
                <p className="text-gray-600">{t('domainSettings.dnsGuide.step2Description')}</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4">
                3
              </div>
              <div>
                <h3 className="font-medium text-gray-900 mb-1">{t('domainSettings.dnsGuide.step3Title')}</h3>
                <p className="text-gray-600">{t('domainSettings.dnsGuide.step3Description')}</p>
              </div>
            </div>
            
            <div className="flex items-start mt-6 p-4 bg-yellow-50 rounded-lg">
              <svg className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-yellow-800 text-sm">{t('domainSettings.dnsGuide.dnsNote')}</p>
            </div>
          </div>
        </div>

        {/* Custom Domain Settings Section - 하단에 배치 */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {t('domainSettings.customDomainTitle')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('domainSettings.customDomainDescription')}
          </p>
          
          <div className="mb-6">
            <label htmlFor="customDomain" className="block text-sm font-medium text-gray-700 mb-2">
              {t('domainSettings.customDomainLabel')}
            </label>
            <input
              type="text"
              id="customDomain"
              value={customDomain}
              onChange={(e) => handleDomainChange(e.target.value)}
              placeholder={t('domainSettings.customDomainPlaceholder')}
              className={`block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm ${
                customDomain.length > 0 && (!isValidDomain || showKoreanWarning) ? 'border-red-300' : ''
              }`}
            />
            {showKoreanWarning && (
              <p className="mt-1 text-sm text-red-600">
                {t('domainSettings.koreanInputDetected')}
              </p>
            )}
            {customDomain.length > 0 && !isValidDomain && (
              <p className="mt-1 text-sm text-red-600">
                {t('domainSettings.domainValidation.invalidFormat')}
              </p>
            )}
            {customDomain.length === 0 && (
              <p className="mt-1 text-sm text-gray-500">
                {t('domainSettings.englishOnlyHelperText')}
              </p>
            )}
          </div>

          {existingDomainData && (
            <div className="mb-6">
              <div className="flex items-center mb-2">
                <span className="text-sm font-medium text-gray-700 mr-2">
                  {t('domainSettings.domainStatus')}:
                </span>
                <span 
                  className="px-2 py-1 text-xs font-medium rounded-full"
                  style={{ 
                    backgroundColor: getStatusColor(existingDomainData.status) + '20',
                    color: getStatusColor(existingDomainData.status)
                  }}
                >
                  {getStatusText(existingDomainData.status)}
                </span>
              </div>
              {existingDomainData.domain && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                    </svg>
                    <span className="text-blue-700 font-medium">
                      {existingDomainData.domain}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex space-x-4">
            <button
              onClick={() => setDnsGuideVisible(true)}
              className="flex-1 py-2 px-4 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50 font-medium"
            >
              {t('domainSettings.dnsGuideButton')}
            </button>
            
            <button
              onClick={saveCustomDomain}
              disabled={!isValidDomain || customDomainLoading}
              className={`flex-1 py-2 px-4 rounded-md text-white font-medium ${
                !isValidDomain || customDomainLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {customDomainLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('domainSettings.saving')}
                </div>
              ) : (
                t('domainSettings.setupCustomDomain')
              )}
            </button>
          </div>

          {/* 도메인 검증 버튼 (기존 도메인이 있는 경우) */}
          {existingDomainData && existingDomainData.status === 'pending' && (
            <div className="mt-4">
              <button
                onClick={verifyCustomDomain}
                disabled={verifyingDomain}
                className={`w-full py-2 px-4 rounded-md text-white font-medium ${
                  verifyingDomain
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {verifyingDomain ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('domainSettings.domainValidation.verifying', '검증 중...')}
                  </div>
                ) : (
                  t('domainSettings.domainValidation.submitForVerification', '수동 검증 요청')
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Snackbar */}
      {snackbarVisible && (
        <div className="fixed bottom-4 right-4 bg-gray-900 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          {snackbarMessage}
        </div>
      )}

      {/* DNS Guide Modal */}
      {dnsGuideVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">{t('domainSettings.dnsGuide.title')}</h3>
              <button
                onClick={() => setDnsGuideVisible(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">{t('domainSettings.dnsGuide.step1Title')}</h4>
                <p className="text-gray-600">{t('domainSettings.dnsGuide.step1Description')}</p>
              </div>
              <div>
                <h4 className="font-medium mb-2">{t('domainSettings.dnsGuide.step2Title')}</h4>
                <p className="text-gray-600">{t('domainSettings.dnsGuide.step2Description')}</p>
              </div>
              <div>
                <h4 className="font-medium mb-2">{t('domainSettings.dnsGuide.step3Title')}</h4>
                <p className="text-gray-600">{t('domainSettings.dnsGuide.step3Description')}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDomainSettingsPage;









